import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, X, Loader2 } from 'lucide-react';

export default function PropertyForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    location: '',
    bedrooms: '',
    bathrooms: '',
    square_feet: '',
    category: 'house',
    status: 'available',
    is_featured: false,
    featured_image: '',
    floor_plan: '',
    video_tour: '',
    images: []
  });

  useEffect(() => {
    if (isEdit) {
      fetchProperty();
    }
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await api.get(`/properties/${id}`);
      const property = response.data;
      setFormData({
        title: property.title,
        description: property.description,
        price: property.price.toString(),
        location: property.location,
        bedrooms: property.bedrooms.toString(),
        bathrooms: property.bathrooms.toString(),
        square_feet: property.square_feet.toString(),
        category: property.category,
        status: property.status,
        featured_image: property.featured_image || '',
        is_featured: !!property.is_featured,
        floor_plan: property.floor_plan || '',
        video_tour: property.video_tour || '',
        images: property.images || []
      });
    } catch (error) {
      console.error('Failed to fetch property:', error);
      alert('Failed to load property');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFeaturedImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      
      const response = await api.post('/upload', formDataObj);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setFormData(prev => ({
        ...prev,
        featured_image: `${backendUrl}${response.data.url}`
      }));
    } catch (error) {
      console.error('Failed to upload featured image:', error);
      alert('Failed to upload featured image');
    } finally {
      setUploading(false);
    }
  };

  const handleFloorPlanUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('file', file);
      
      const response = await api.post('/upload', formDataObj);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      setFormData(prev => ({
        ...prev,
        floor_plan: `${backendUrl}${response.data.url}`
      }));
    } catch (error) {
      console.error('Failed to upload floor plan:', error);
      alert('Failed to upload floor plan');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadedUrls = [];
      for (const file of files) {
        const formDataObj = new FormData();
        formDataObj.append('file', file);
        
        // Don't set Content-Type header manually - let axios handle it with boundary
        const response = await api.post('/upload', formDataObj);
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        uploadedUrls.push(`${backendUrl}${response.data.url}`);
      }
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
    } catch (error) {
      console.error('Failed to upload images:', error);
      alert('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const propertyData = {
        title: formData.title,
        description: formData.description,
        price: parseFloat(formData.price),
        location: formData.location,
        bedrooms: parseInt(formData.bedrooms),
        bathrooms: parseInt(formData.bathrooms),
        square_feet: parseInt(formData.square_feet),
        category: formData.category,
        status: formData.status,
        is_featured: !!formData.is_featured,
        featured_image: formData.featured_image || null,
        floor_plan: formData.floor_plan || null,
        video_tour: formData.video_tour || null,
        images: formData.images
      };

      if (isEdit) {
        await api.put(`/properties/${id}`, propertyData);
      } else {
        await api.post('/properties', propertyData);
      }

      navigate('/dashboard/properties');
    } catch (error) {
      console.error('Failed to save property:', error);
      alert(error.response?.data?.detail || 'Failed to save property');
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight" data-testid="property-form-title">
            {isEdit ? 'Edit Property' : 'Add New Property'}
          </h1>
          <p className="text-muted-foreground">Fill in the details below</p>
        </div>

        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle>Property Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  data-testid="property-title-input"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  required
                  data-testid="property-description-input"
                />
              </div>

              {/* Price and Location */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price ($) *</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    value={formData.price}
                    onChange={handleChange}
                    required
                    data-testid="property-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleChange}
                    required
                    data-testid="property-location-input"
                  />
                </div>
              </div>

              {/* Bedrooms, Bathrooms, Square Feet */}
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bedrooms">Bedrooms *</Label>
                  <Input
                    id="bedrooms"
                    name="bedrooms"
                    type="number"
                    value={formData.bedrooms}
                    onChange={handleChange}
                    required
                    data-testid="property-bedrooms-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bathrooms">Bathrooms *</Label>
                  <Input
                    id="bathrooms"
                    name="bathrooms"
                    type="number"
                    value={formData.bathrooms}
                    onChange={handleChange}
                    required
                    data-testid="property-bathrooms-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="square_feet">Square Feet *</Label>
                  <Input
                    id="square_feet"
                    name="square_feet"
                    type="number"
                    value={formData.square_feet}
                    onChange={handleChange}
                    required
                    data-testid="property-sqft-input"
                  />
                </div>
              </div>

              {/* Category and Status */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger data-testid="property-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="house">House</SelectItem>
                      <SelectItem value="apartment">Apartment</SelectItem>
                      <SelectItem value="condo">Condo</SelectItem>
                      <SelectItem value="land">Land</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status *</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger data-testid="property-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="sold">Sold</SelectItem>
                    </SelectContent>
                  </Select>
              {/* Featured toggle */}
              <div className="flex items-center gap-2">
                <Label htmlFor="is_featured">Featured</Label>
                <input
                  id="is_featured"
                  type="checkbox"
                  checked={!!formData.is_featured}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_featured: e.target.checked }))}
                />
              </div>

                </div>
              </div>

              {/* Featured Image */}
              <div className="space-y-2">
                <Label>Featured Image *</Label>
                <p className="text-xs text-gray-500 mb-2">This image will be displayed on the property card</p>
                {formData.featured_image ? (
                  <div className="relative inline-block w-full">
                    <img
                      src={formData.featured_image}
                      alt="Featured"
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, featured_image: '' }))}
                      className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 hover:bg-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ) : (
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                    <div className="text-center">
                      <Upload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="mt-4">
                        <label htmlFor="featured-image-upload" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            Upload featured image
                          </span>
                          <input
                            id="featured-image-upload"
                            type="file"
                            accept="image/*"
                            onChange={handleFeaturedImageUpload}
                            className="hidden"
                            disabled={uploading}
                            data-testid="property-featured-image-upload"
                          />
                        </label>
                        <p className="mt-1 text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                      </div>
                      {uploading && (
                        <div className="mt-4 flex items-center justify-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span className="text-sm">Uploading...</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Images */}
              <div className="space-y-2">
                <Label>Additional Images</Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                  <div className="text-center">
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="mt-4">
                      <label htmlFor="file-upload" className="cursor-pointer">
                        <span className="mt-2 block text-sm font-medium text-gray-900">
                          Upload images
                        </span>
                        <input
                          id="file-upload"
                          type="file"
                          multiple
                          accept="image/*"
                          onChange={handleFileUpload}
                          className="hidden"
                          disabled={uploading}
                          data-testid="property-image-upload"
                        />
                      </label>
                      <p className="mt-1 text-xs text-gray-500">PNG, JPG, GIF up to 10MB</p>
                    </div>
                    {uploading && (
                      <div className="mt-4 flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm">Uploading...</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Image Preview */}
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-4 mt-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={image}
                          alt={`Property ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Floor Plan - Only show for non-land properties */}
              {formData.category !== 'land' && (
                <div className="space-y-2">
                  <Label>Floor Plan</Label>
                  <p className="text-xs text-gray-500 mb-2">Upload the property floor plan image</p>
                  {formData.floor_plan ? (
                    <div className="relative inline-block w-full">
                      <img
                        src={formData.floor_plan}
                        alt="Floor Plan"
                        className="w-full h-64 object-contain bg-gray-50 rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, floor_plan: '' }))}
                        className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 hover:bg-red-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                      <div className="text-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="mt-4">
                          <label htmlFor="floor-plan-upload" className="cursor-pointer">
                            <span className="mt-2 block text-sm font-medium text-gray-900">
                              Upload floor plan
                            </span>
                            <input
                              id="floor-plan-upload"
                              type="file"
                              accept="image/*"
                              onChange={handleFloorPlanUpload}
                              className="hidden"
                              disabled={uploading}
                              data-testid="property-floor-plan-upload"
                            />
                          </label>
                          <p className="mt-1 text-xs text-gray-500">PNG, JPG, PDF up to 10MB</p>
                        </div>
                        {uploading && (
                          <div className="mt-4 flex items-center justify-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-sm">Uploading...</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Video Tour */}
              <div className="space-y-2">
                <Label htmlFor="video_tour">Video Tour URL</Label>
                <Input
                  id="video_tour"
                  name="video_tour"
                  type="url"
                  placeholder="https://youtube.com/watch?v=..."
                  value={formData.video_tour}
                  onChange={handleChange}
                  data-testid="property-video-tour-input"
                />
                <p className="text-xs text-gray-500">YouTube, Vimeo, or direct video link</p>
              </div>

              {/* Submit Buttons */}
              <div className="flex gap-4 pt-4">
                <Button type="submit" disabled={loading} data-testid="property-submit-button">
                  {loading ? 'Saving...' : isEdit ? 'Update Property' : 'Create Property'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/dashboard/properties')}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </DashboardLayout>
  );
}
